import { openDB } from "idb";
import { rootVariantKeys, mergeSearchableRoots, maqsuraFallbackKey } from "./arabicNormalize";

const DB_NAME = "hanswehr";
// The dictionary lives in one of two identical stores; meta.state.active names the live
// one. A data update (dictionary-update.js) imports into the OTHER store and then flips
// `active` — so lookups never stall on, or see, a half-written import.
const ENTRY_STORES = ["entries_a", "entries_b"];
const META_STORE = "meta";
const LEGACY_STORE = "entries"; // DB_VERSION <= 4
// Bump ONLY when the store/index schema or the search-key normalization in
// arabicNormalize.js changes (installs then rebuild from the bundled JSON and pick the
// latest data up again over the air). New dictionary DATA no longer needs a bump: it
// ships as a versioned snapshot that dictionary-update.js installs in the background.
// v4: keys are normalized like queries, so punctuation-only tokens, tatweel, bare
// hamza and Persian letter forms are gone. (v3 was a pre-release that also folded ى
// into ي; skipping it rebuilds those test installs.)
// v5: two entry stores + meta (over-the-air data updates).
const DB_VERSION = 5;
// Bundled fallback data and its manifest (version + sha256), refreshed by `yarn data:pull`.
const BUNDLED_JSON_URL = "/data/hans_wehr_data.json";
const BUNDLED_MANIFEST_URL = "/data/data-manifest.json";
const RESPONSE_VERS = "1.0";

// A Latin letter left in a search key is one the letter map couldn't convert.
const LATIN_LETTER = /\p{Script=Latin}/u;

// Shown while another tab holds the dictionary open on an older version of the app
// and stalls this tab's upgrade (which every lookup waits for). Resolves to a
// function that takes the notice down again.
const showUpgradeBlockedNotice = () =>
  import("./utils")
    .then(({ toastWaiting }) =>
      toastWaiting("Update waiting", "Close other Hans Wehr tabs to finish updating the dictionary.")
    )
    .catch(() => () => {});

let dbPromise = null;
let initPromise = null; // Holds the single init call
let activeStore = ENTRY_STORES[0]; // the live entry store (from meta.state.active)
// After a failed init, fail fast for a while instead of re-fetching and re-parsing
// the ~10 MB dictionary JSON on every lookup / keystroke.
const INIT_RETRY_COOLDOWN_MS = 30000;
let lastInitFailure = null; // { err, at }

/**
 * Initializes the IndexedDB database only once.
 */
export async function initDictionaryDB(jsonURL = BUNDLED_JSON_URL) {
  if (initPromise) {
    return initPromise; // Return ongoing or completed init
  }
  if (lastInitFailure && Date.now() - lastInitFailure.at < INIT_RETRY_COOLDOWN_MS) {
    throw lastInitFailure.err;
  }

  console.log("📦 Initializing IndexedDB...");
  initPromise = (async () => {
    let blockedNotice = null;
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      // A tab on an older version keeps the dictionary open, so this upgrade waits.
      blocked() {
        if (!blockedNotice) blockedNotice = showUpgradeBlockedNotice();
      },
      // This tab is the older one: a newer version opened in another tab. Let go of
      // the dictionary at once, or that tab can't upgrade until this one is closed,
      // and reload into the new version (this one can't open the upgraded store).
      blocking(currentVersion, blockedVersion, event) {
        event.target.close();
        window.location.reload();
      },
      upgrade(db) {
        // (Re)build every store from scratch on every version bump. Dropping them
        // empties the cache, so the `count === 0` path below re-imports the bundled
        // JSON and rebuilds the indexes to the current schema (the update check then
        // brings the latest published data back). Only the dictionary lives in this DB
        // — bookmarks/flashcards are kept in localStorage / Drive sync — so this never
        // touches user data.
        for (const name of [LEGACY_STORE, ...ENTRY_STORES, META_STORE]) {
          if (db.objectStoreNames.contains(name)) db.deleteObjectStore(name);
        }
        for (const name of ENTRY_STORES) {
          const store = db.createObjectStore(name, { keyPath: "word_ID" });
          store.createIndex("root", "root");
          store.createIndex("searchableRoots", "searchableRoots", { multiEntry: true });
        }
        db.createObjectStore(META_STORE); // out-of-line keys: "state"
        try {
          localStorage.removeItem("hw-sr-aug-1"); // retired backfill flag (<= v4)
        } catch {
          /* storage unavailable */
        }
        console.log("🛠️ Stores (re)built; dictionary will (re)import");
      },
    });

    const db = await dbPromise.finally(() => blockedNotice?.then((close) => close()));
    const state = await db.get(META_STORE, "state");
    if (state?.active && ENTRY_STORES.includes(state.active)) activeStore = state.active;
    const count = await db.count(activeStore);
    console.log(`📊 Dictionary entries in DB: ${count} (${activeStore}, data ${state?.dataVersion ?? "?"})`);

    if (count === 0) {
      console.log("⬇️ Fetching dictionary JSON...");
      const [data, manifest] = await Promise.all([
        fetch(jsonURL).then((r) => r.json()),
        fetch(BUNDLED_MANIFEST_URL)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
      ]);
      console.log(`📥 Loaded ${data.length} entries from JSON`);
      await writeEntries(db, activeStore, data);
      await db.put(META_STORE, stateFrom(manifest, activeStore, data.length, "bundled"), "state");
      console.log("✅ Dictionary data written to IndexedDB");
    } else {
      console.log("✅ Dictionary already loaded");
    }

    lastInitFailure = null;
    return dbPromise;
  })().catch((err) => {
    // Don't cache the failure forever: a rejected initPromise made every later lookup
    // fail until the app was killed (e.g. one QuotaExceeded during the first import).
    // Allow a retry after the cooldown, and close the half-open connection.
    const dead = dbPromise;
    initPromise = null;
    dbPromise = null;
    lastInitFailure = { err, at: Date.now() };
    dead?.then((db) => db.close(), () => {});
    throw err;
  });

  return initPromise;
}

// One transaction: clear + put every entry. Atomic — on failure the store is left as it
// was (empty on first run, or the previous inactive copy during an update).
async function writeEntries(db, storeName, data) {
  const tx = db.transaction(storeName, "readwrite");
  tx.store.clear();
  for (const entry of data) {
    // Index every alternate spelling derived from the root field (slash/space
    // compounds, hamza seats), on top of the JSON's own searchableRoots.
    entry.searchableRoots = mergeSearchableRoots(entry.searchableRoots, entry.root);
    tx.store.put(entry);
  }
  await tx.done;
}

function stateFrom(manifest, active, entries, source) {
  return {
    active,
    dataVersion: manifest?.version ?? null,
    sha256: manifest?.sha256 ?? null,
    schema: manifest?.schema ?? null,
    label: manifest?.label ?? "",
    entries,
    source, // "bundled" | "remote"
    installedAt: Date.now(),
  };
}

/** The installed dictionary data: { active, dataVersion, sha256, entries, source, … }. */
export async function getDictionaryState() {
  await initDictionaryDB();
  const db = await dbPromise;
  return (await db.get(META_STORE, "state")) || null;
}

/**
 * Installs a new dictionary snapshot (already downloaded + verified by
 * dictionary-update.js): imports into the inactive store, then flips meta.state.active.
 * Lookups keep using the old store until the flip, so nothing ever sees a partial import.
 */
export async function installDictionaryData(data, manifest) {
  await initDictionaryDB();
  const db = await dbPromise;
  const target = activeStore === ENTRY_STORES[0] ? ENTRY_STORES[1] : ENTRY_STORES[0];
  await writeEntries(db, target, data);
  const state = stateFrom(manifest, target, data.length, "remote");
  await db.put(META_STORE, state, "state");
  const previous = activeStore;
  switchEntryStore(target);
  // Free the old copy later (other tabs switch on the broadcast first).
  setTimeout(() => {
    if (activeStore !== previous) {
      const tx = db.transaction(previous, "readwrite");
      tx.store.clear();
      tx.done.catch(() => {});
    }
  }, 60000);
  return state;
}

/** Point lookups at `name` (after an install here or in another tab). */
export function switchEntryStore(name) {
  if (!ENTRY_STORES.includes(name) || name === activeStore) return;
  activeStore = name;
  _randomPoolCache = null;
  try {
    // Other open tabs follow via the `storage` event below (fires in every other tab).
    localStorage.setItem(ACTIVE_KEY, name);
  } catch {
    /* storage unavailable */
  }
}

const ACTIVE_KEY = "hw-dict-active";
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === ACTIVE_KEY && e.newValue && ENTRY_STORES.includes(e.newValue)) {
      activeStore = e.newValue;
      _randomPoolCache = null;
    }
  });
}

/**
 * Retrieves and formats all words that have the given root. The root may be a raw
 * or compound spelling ("جلو / جلى", a hamza-seat variant, …); each alternate is
 * normalized and looked up, and the matches are unioned.
 */
export async function retrieveAllWordsWithRoot(root) {
  console.log(`🔍 Searching IndexedDB for root: "${root}" via searchableRoots`);

  // ✅ Ensure DB is initialized before querying
  await initDictionaryDB();

  const db = await dbPromise;

  // Latin letters left in a key are ones the letter map couldn't convert ("book" ->
  // "بooك"). Normalizing would strip them and match an unrelated root (بك), so treat
  // the query as not found instead.
  if (LATIN_LETTER.test(root || "")) return [];

  let keys = rootVariantKeys(root);
  if (!keys.length) keys = [root];

  const seen = new Set();
  const results = [];
  const lookUp = async (key) => {
    const docs = await db.getAllFromIndex(activeStore, "searchableRoots", key);
    for (const doc of docs) {
      if (!seen.has(doc.word_ID)) {
        seen.add(doc.word_ID);
        results.push(doc);
      }
    }
  };
  for (const key of keys) await lookUp(key);
  // A root typed in its citation form (رمى) that the dictionary keys with ي (رمي).
  if (!results.length) {
    for (const key of keys) {
      const alt = maqsuraFallbackKey(key);
      if (alt) await lookUp(alt);
    }
  }
  console.log(`📑 Found ${results.length} entries for root "${root}"`);

  const response = results.map((doc) => {
    const form_definitions = (doc.forms || []).map((word) => ({
      id: word.id,
      text: word.text,
      form: word.form,
      transliteration: word.transliteration,
      translation: {
        id: word.translation.id,
        text: word.translation.text,
        short: word.translation.short,
      },
    }));

    const noun_definitions = (doc.nouns || []).map((word) => ({
      id: word.id,
      text: word.text,
      transliteration: word.transliteration,
      plural: word.plural,
      translation: {
        id: word.translation.id,
        text: word.translation.text,
        short: word.translation.short,
      },
    }));

    return {
      word: root,
      // The dictionary's own spelling of the root (سأل, قرأ), which the search key
      // (سال, قرا) has lost: the word page's title and banners, and the bookmark and
      // history lists, show this.
      root: doc.root,
      rootInfo: doc.forms?.[0]?.root || null,
      definitions: form_definitions,
      nouns: noun_definitions,
      responseVersion: RESPONSE_VERS,
    };
  });

  console.log(`🧾 Structured response count: ${response.length}`);
  return response;
}

/**
 * Given candidate root keys (already in searchableRoots form), returns the
 * subset that actually exist in the dictionary, preserving input order.
 * Uses the searchableRoots index (getKey) so it's O(log n) per root rather
 * than a full-table scan. All lookups are fired on one transaction in the same
 * tick to keep it active across the awaits.
 */
export async function rootsExistInDataset(roots) {
  if (!roots || roots.length === 0) return [];
  await initDictionaryDB();
  const db = await dbPromise;

  const index = db.transaction(activeStore).store.index("searchableRoots");
  const keys = await Promise.all(roots.map((r) => index.getKey(r)));
  return roots.filter((_, i) => keys[i] !== undefined);
}

/**
 * Given candidate root keys (searchableRoots form), returns a Map of each key that
 * exists to the raw entries indexed under it, in input order. Used to rank root
 * suggestions by whether an entry actually lists the searched word.
 */
export async function getEntriesForRootKeys(roots) {
  const out = new Map();
  if (!roots || roots.length === 0) return out;
  await initDictionaryDB();
  const db = await dbPromise;

  const index = db.transaction(activeStore).store.index("searchableRoots");
  const docs = await Promise.all(roots.map((r) => index.getAll(r)));
  roots.forEach((r, i) => {
    if (docs[i].length > 0) out.set(r, docs[i]);
  });
  return out;
}

// Cached after first call \u2014 avoids repeated full-table scans for random entry
let _randomPoolCache = null;

/**
 * Returns a random root entry (entries that have a proper Arabic root and a main form with translation).
 */
export async function getRandomEntry() {
  await initDictionaryDB();
  const db = await dbPromise;

  if (!_randomPoolCache) {
    const all = await db.getAll(activeStore);
    _randomPoolCache = all.filter((e) => {
      if (!e.root || !e.forms?.length || !e.forms[0]?.translation?.text) return false;
      const letters = e.root.replace(/\s+/g, "").replace(/[^\u0600-\u06FF]/g, "");
      return letters.length >= 3 && letters.length <= 4;
    });
  }

  if (!_randomPoolCache.length) return null;
  return _randomPoolCache[Math.floor(Math.random() * _randomPoolCache.length)];
}

/**
 * Returns a list of unique searchable roots that match the input prefix.
 * Uses a keyCursor on the searchableRoots multiEntry index for an O(log n)
 * range scan instead of loading all entries into memory.
 * cursor.key gives the index key value (the root string itself).
 */
export async function getMatchingRoots(query) {
  // An empty prefix (e.g. input that was only harakat) would list every key.
  if (!query) return [];
  await initDictionaryDB();
  const db = await dbPromise;

  const index = db.transaction(activeStore).store.index("searchableRoots");
  const roots = new Set();
  const scan = async (prefix) => {
    // ￿ is the highest BMP code point — bounds the scan to keys starting with prefix
    let cursor = await index.openKeyCursor(IDBKeyRange.bound(prefix, prefix + "￿"));
    while (cursor) {
      roots.add(cursor.key); // cursor.key is the root string, not the primary key
      cursor = await cursor.continue();
    }
  };
  await scan(query);
  // رمى also offers رمي, the key the lookup falls back to (see retrieveAllWordsWithRoot).
  const alt = maqsuraFallbackKey(query);
  if (alt) await scan(alt);

  return [...roots].sort();
}
